"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.binarySearch = binarySearch;
exports.equals = equals;
exports.extend = extend;
exports.find = find;
exports.findIndex = findIndex;
exports.includes = includes;
exports.isSorted = isSorted;
exports.linearFindNearest = linearFindNearest;
exports.numberSafeCompareFunction = numberSafeCompareFunction;
exports.remove = remove;
exports.reverseSubArray = reverseSubArray;
exports.stableSort = stableSort;

/**
 * @module ol/array
 */

/**
 * Performs a binary search on the provided sorted list and returns the index of the item if found. If it can't be found it'll return -1.
 * https://github.com/darkskyapp/binary-search
 *
 * @param {Array<*>} haystack Items to search through.
 * @param {*} needle The item to look for.
 * @param {Function} [opt_comparator] Comparator function.
 * @return {number} The index of the item if found, -1 if not.
 */
function binarySearch(haystack, needle, opt_comparator) {
  var mid, cmp;
  var comparator = opt_comparator || numberSafeCompareFunction;
  var low = 0;
  var high = haystack.length;
  var found = false;

  while (low < high) {
    /* Note that "(low + high) >>> 1" may overflow, and results in a typecast
     * to double (which gives the wrong results). */
    mid = low + (high - low >> 1);
    cmp = +comparator(haystack[mid], needle);

    if (cmp < 0.0) {
      /* Too low. */
      low = mid + 1;
    } else {
      /* Key found or too high */
      high = mid;
      found = !cmp;
    }
  }
  /* Key not found. */


  return found ? low : ~low;
}
/**
 * Compare function for array sort that is safe for numbers.
 * @param {*} a The first object to be compared.
 * @param {*} b The second object to be compared.
 * @return {number} A negative number, zero, or a positive number as the first
 *     argument is less than, equal to, or greater than the second.
 */


function numberSafeCompareFunction(a, b) {
  return a > b ? 1 : a < b ? -1 : 0;
}
/**
 * Whether the array contains the given object.
 * @param {Array<*>} arr The array to test for the presence of the element.
 * @param {*} obj The object for which to test.
 * @return {boolean} The object is in the array.
 */


function includes(arr, obj) {
  return arr.indexOf(obj) >= 0;
}
/**
 * {@link module:ol/tilegrid/TileGrid~TileGrid#getZForResolution} can use a function
 * of this type to determine which nearest resolution to use.
 *
 * This function takes a `{number}` representing a value between two array entries,
 * a `{number}` representing the value of the nearest higher entry and
 * a `{number}` representing the value of the nearest lower entry
 * as arguments and returns a `{number}`. If a negative number or zero is returned
 * the lower value will be used, if a positive number is returned the higher value
 * will be used.
 * @typedef {function(number, number, number): number} NearestDirectionFunction
 * @api
 */

/**
 * @param {Array<number>} arr Array in descending order.
 * @param {number} target Target.
 * @param {number|NearestDirectionFunction} direction
 *    0 means return the nearest,
 *    > 0 means return the largest nearest,
 *    < 0 means return the smallest nearest.
 * @return {number} Index.
 */


function linearFindNearest(arr, target, direction) {
  var n = arr.length;

  if (arr[0] <= target) {
    return 0;
  } else if (target <= arr[n - 1]) {
    return n - 1;
  } else {
    var i = void 0;

    if (direction > 0) {
      for (i = 1; i < n; ++i) {
        if (arr[i] < target) {
          return i - 1;
        }
      }
    } else if (direction < 0) {
      for (i = 1; i < n; ++i) {
        if (arr[i] <= target) {
          return i;
        }
      }
    } else {
      for (i = 1; i < n; ++i) {
        if (arr[i] == target) {
          return i;
        } else if (arr[i] < target) {
          if (typeof direction === 'function') {
            if (direction(target, arr[i - 1], arr[i]) > 0) {
              return i - 1;
            } else {
              return i;
            }
          } else if (arr[i - 1] - target < target - arr[i]) {
            return i - 1;
          } else {
            return i;
          }
        }
      }
    }

    return n - 1;
  }
}
/**
 * @param {Array<*>} arr Array.
 * @param {number} begin Begin index.
 * @param {number} end End index.
 */


function reverseSubArray(arr, begin, end) {
  while (begin < end) {
    var tmp = arr[begin];
    arr[begin] = arr[end];
    arr[end] = tmp;
    ++begin;
    --end;
  }
}
/**
 * @param {Array<VALUE>} arr The array to modify.
 * @param {!Array<VALUE>|VALUE} data The elements or arrays of elements to add to arr.
 * @template VALUE
 */


function extend(arr, data) {
  var extension = Array.isArray(data) ? data : [data];
  var length = extension.length;

  for (var i = 0; i < length; i++) {
    arr[arr.length] = extension[i];
  }
}
/**
 * @param {Array<VALUE>} arr The array to modify.
 * @param {VALUE} obj The element to remove.
 * @template VALUE
 * @return {boolean} If the element was removed.
 */


function remove(arr, obj) {
  var i = arr.indexOf(obj);
  var found = i > -1;

  if (found) {
    arr.splice(i, 1);
  }

  return found;
}
/**
 * @param {Array<VALUE>} arr The array to search in.
 * @param {function(VALUE, number, ?) : boolean} func The function to compare.
 * @template VALUE
 * @return {VALUE|null} The element found or null.
 */


function find(arr, func) {
  var length = arr.length >>> 0;
  var value;

  for (var i = 0; i < length; i++) {
    value = arr[i];

    if (func(value, i, arr)) {
      return value;
    }
  }

  return null;
}
/**
 * @param {Array|Uint8ClampedArray} arr1 The first array to compare.
 * @param {Array|Uint8ClampedArray} arr2 The second array to compare.
 * @return {boolean} Whether the two arrays are equal.
 */


function equals(arr1, arr2) {
  var len1 = arr1.length;

  if (len1 !== arr2.length) {
    return false;
  }

  for (var i = 0; i < len1; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}
/**
 * Sort the passed array such that the relative order of equal elements is preserved.
 * See https://en.wikipedia.org/wiki/Sorting_algorithm#Stability for details.
 * @param {Array<*>} arr The array to sort (modifies original).
 * @param {!function(*, *): number} compareFnc Comparison function.
 * @api
 */


function stableSort(arr, compareFnc) {
  var length = arr.length;
  var tmp = Array(arr.length);
  var i;

  for (i = 0; i < length; i++) {
    tmp[i] = {
      index: i,
      value: arr[i]
    };
  }

  tmp.sort(function (a, b) {
    return compareFnc(a.value, b.value) || a.index - b.index;
  });

  for (i = 0; i < arr.length; i++) {
    arr[i] = tmp[i].value;
  }
}
/**
 * @param {Array<*>} arr The array to search in.
 * @param {Function} func Comparison function.
 * @return {number} Return index.
 */


function findIndex(arr, func) {
  var index;
  var found = !arr.every(function (el, idx) {
    index = idx;
    return !func(el, idx, arr);
  });
  return found ? index : -1;
}
/**
 * @param {Array<*>} arr The array to test.
 * @param {Function} [opt_func] Comparison function.
 * @param {boolean} [opt_strict] Strictly sorted (default false).
 * @return {boolean} Return index.
 */


function isSorted(arr, opt_func, opt_strict) {
  var compare = opt_func || numberSafeCompareFunction;
  return arr.every(function (currentVal, index) {
    if (index === 0) {
      return true;
    }

    var res = compare(arr[index - 1], currentVal);
    return !(res > 0 || opt_strict && res === 0);
  });
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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